import type { IntentRead } from '../../types/api';

/**
 * Reading a row's intents back in words.
 *
 * Plain functions over the registry the server answered with — no React, no
 * API — so the four surfaces that *state* an intent (the review spec sheet,
 * the variant cards, the history rows, the pickers) all say the same thing
 * about the same keys.
 *
 * Nothing here knows a key by name. An unresolved list is reported as such
 * rather than guessed at: the registry is the only place a key becomes words.
 */

/** How many intents one request may carry. The server refuses a third. */
export const MAX_INTENTS = 2;

/**
 * The labels for `keys`, in pick order.
 *
 * Empty when there is nothing to say *or* when the registry has not arrived
 * yet — the caller cannot tell those apart from this alone, which is why the
 * summary below exists. A key the loaded registry does not know falls back to
 * the key itself: a retired intent still happened, and hiding it would make
 * the row a lie.
 */
export function intentLabels(keys: readonly string[], registry: readonly IntentRead[]): string[] {
  if (keys.length === 0 || registry.length === 0) return [];
  return keys.map((key) => registry.find((intent) => intent.key === key)?.label ?? key);
}

/**
 * What a row's intents say, as one line: "Point of view + Lead attraction", or
 * "Model's choice" when the brief named none.
 *
 * Null means "not resolvable yet" — keys are on the row but the registry is
 * still loading — and a caller renders nothing at all rather than flashing raw
 * keys at the user.
 */
export function intentSummary(
  keys: readonly string[],
  registry: readonly IntentRead[],
): string | null {
  if (keys.length === 0) return 'Our pick';
  const labels = intentLabels(keys, registry);
  return labels.length === 0 ? null : labels.join(' + ');
}

/**
 * The intent one variant leads with, for a brief that named two.
 *
 * The pipeline's own split: variant 1 leads with the primary and variant 2
 * with the secondary, which is exactly why two drafts off a two-intent brief
 * are worth comparing. Null for a brief with fewer than two intents — there is
 * no difference to point at — and null while the registry is still loading.
 */
export function leadIntentLabel(
  keys: readonly string[],
  registry: readonly IntentRead[],
  variantIndex: 1 | 2,
): string | null {
  if (keys.length < MAX_INTENTS) return null;
  const labels = intentLabels(keys, registry);
  return labels[variantIndex - 1] ?? null;
}

/**
 * The keys of `candidates` worth sending: strings, deduped, capped, and — once
 * the registry is on hand — known to it.
 *
 * A stored draft and a row written months ago are both untrusted input, and an
 * intent can be retired between two visits, so a key the registry has dropped
 * is dropped here too rather than sent for the server to refuse with a 422.
 *
 * A registry that has *not* arrived holds no opinion, so the keys ride through
 * untouched: a form saved in that window must not quietly wipe the purposes
 * already on the row, and the server is the judge of them anyway. Nothing is
 * shown as picked during that window either — the picker draws no chips
 * without a registry — so the UI never disagrees with what is sent.
 */
export function knownIntents(
  candidates: readonly unknown[],
  registry: readonly IntentRead[],
  max: number = MAX_INTENTS,
): string[] {
  const kept: string[] = [];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    if (kept.includes(candidate)) continue;
    if (registry.length > 0 && !registry.some((intent) => intent.key === candidate)) continue;
    kept.push(candidate);
    if (kept.length >= max) break;
  }
  return kept;
}
